// BenfordChart.jsx
import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import "../BenFordStyle.css";

/**
 * BenfordChart
 * - Bars remain ordered by Actualvalue (descending).
 * - Benford curve is drawn in descending order independently (monotonic).
 * Props:
 *  - data: array of objects (raw or normalized)
 *  - groupLabel, valueLabel: strings
 *  - curveMode: "both" | "align_to_actual" | "sort_benford_desc"
 *      "both" (default) => bars use Actual order; curve is monotonic (highest->lowest)
 *      "align_to_actual" => both bars and curve follow Actual ordering
 *      "sort_benford_desc" => bars and curve are ordered by benford desc (old behavior)
 */
const BenfordChart = ({
  data = [],
  groupLabel = "Group",
  valueLabel = "Value",
  curveMode = "both",
}) => {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const roRef = useRef(null);
  const [currentRange, setCurrentRange] = useState({ start: 0, end: 50 });
  const [visibleData, setVisibleData] = useState([]);

  const colors = {
    primary: "#3498db",
    secondary: "#e74c3c",
    background: "transparent",
    gridLines: "#ecf0f1",
    textPrimary: "#000",
    textSecondary: "#7f8c8d",
  };

  // Normalize incoming data to only the fields we care about
  useEffect(() => {
    if (!Array.isArray(data)) {
      setVisibleData([]);
      return;
    }

    const normalized = data.map((item) => {
      const raw =
        item && (item.__raw ?? item.row ?? item)
          ? item.__raw ?? item.row ?? item
          : item;

      const Actual =
        item.Actual ??
        item.actual ??
        raw.Actual ??
        raw.actual ??
        raw["Actual"] ??
        null;

      const Actualvalue =
        item.Actualvalue ??
        item.ActualValue ??
        item.actualvalue ??
        raw.Actualvalue ??
        raw.ActualValue ??
        raw.actualvalue ??
        raw.actualValue ??
        null;

      const Benford =
        item.Benford ?? item.benford ?? raw.Benford ?? raw.benford ?? null;

      return {
        Actual,
        Actualvalue,
        Benford,
        __raw: raw,
      };
    });

    const start = Math.max(0, currentRange.start || 0);
    const end = Math.min(
      currentRange.end || normalized.length,
      normalized.length
    );
    const sliced = normalized.slice(start, end);

    // Default ordering for visibleData: sort by Actualvalue desc (keeps bars meaningful)
    const sortedByActual = [...sliced].sort((a, b) => {
      const va = Number.parseFloat(a.Actualvalue) || 0;
      const vb = Number.parseFloat(b.Actualvalue) || 0;
      return vb - va;
    });

    setVisibleData(sortedByActual);
  }, [data, currentRange.start, currentRange.end]);

  // Init chart
  useEffect(() => {
    if (!ref.current) return;
    chartRef.current = echarts.init(ref.current, undefined, {
      renderer: "canvas",
    });

    if (window.ResizeObserver) {
      roRef.current = new ResizeObserver(() => {
        try {
          chartRef.current && chartRef.current.resize();
        } catch (e) {}
      });
      roRef.current.observe(ref.current);
    } else {
      const handle = () => {
        try {
          chartRef.current && chartRef.current.resize();
        } catch (e) {}
      };
      window.addEventListener("resize", handle);
      roRef.current = {
        cleanup: () => window.removeEventListener("resize", handle),
      };
    }

    return () => {
      try {
        roRef.current?.disconnect?.();
      } catch {}
      try {
        roRef.current?.cleanup?.();
      } catch {}
      try {
        chartRef.current?.dispose();
      } catch {}
      chartRef.current = null;
    };
  }, []);

  // Update chart when visibleData or curveMode changes
  useEffect(() => {
    if (!chartRef.current || !Array.isArray(visibleData)) return;

    if (visibleData.length === 0) {
      try {
        chartRef.current.clear();
      } catch (e) {}
      return;
    }

    // Build base array of objects: { category, actualValue, benfordValue }
    const base = visibleData.map((it, idx) => {
      const cat = it.Actual ?? `Item ${idx + 1}`;
      const actualValue = Number.isFinite(parseFloat(it.Actualvalue))
        ? parseFloat(it.Actualvalue)
        : 0;
      const benfordValue = Number.isFinite(parseFloat(it.Benford))
        ? parseFloat(it.Benford)
        : 0;
      return {
        category: cat,
        actualValue,
        benfordValue,
        __raw: it.__raw ?? it,
      };
    });

    const n = base.length;

    // Prepare series data depending on curveMode
    let barCategories = base.map((p) => {
      // Convert category labels to integers if possible
      const val = Number(p.category);
      return Number.isFinite(val) ? Math.round(val) : p.category;
    }); // bars always use this
    let barData = base.map((p) => p.actualValue);

    let lineData = []; // for line on same category axis (if used)
    let lineDataNumeric = []; // for monotonic curve on numeric xAxis

    if (curveMode === "align_to_actual") {
      // both bar and line aligned to current base order
      lineData = base.map((p) => p.benfordValue);
    } else if (curveMode === "sort_benford_desc") {
      // reorder base by benford desc so both follow same ordering
      const byBenford = [...base].sort(
        (a, b) => b.benfordValue - a.benfordValue
      );
      barCategories = byBenford.map((p) => p.category);
      barData = byBenford.map((p) => p.actualValue);
      lineData = byBenford.map((p) => p.benfordValue);
    } else {
      // "both": keep bars ordered by Actual; draw monotonic benford curve separately on numeric x-axis
      // Build benfordSorted descending
      const benfordSorted = [...base]
        .sort((a, b) => b.benfordValue - a.benfordValue)
        .map((p) => p.benfordValue);
      // lineDataNumeric: [[0, v0], [1, v1], ...]
      lineDataNumeric = benfordSorted.map((v, i) => [i, v]);
      // For tooltip consistency, we also provide lineData (by index) but using nulls won't draw; we will set lineSeries with xAxisIndex:1
      lineData = []; // not used for category-aligned line when using numeric axis
    }

    const maxVal = Math.max(
      ...barData,
      ...(lineData.length ? lineData : []),
      ...(lineDataNumeric.length ? lineDataNumeric.map((d) => d[1]) : []),
      0
    );

    const option = {
      backgroundColor: colors.background,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: "#ffffff",
        borderColor: colors.gridLines,
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { color: colors.textPrimary, fontSize: 12 },
        formatter: (params) => {
          // params may contain multiple series. We construct a friendly tooltip.
          if (!params || !params.length) return "";
          // Try find bar param
          const barParam = params.find((p) => p.seriesType === "bar");
          const lineParam = params.find(
            (p) => p.seriesType === "line" && p.seriesIndex === 1
          ); // might be numeric axis
          let idx = barParam
            ? barParam.dataIndex
            : params[0]
            ? params[0].dataIndex
            : 0;
          let linestr = "";
          if (barParam) {
            const item = {
              category: barCategories[idx],
              actualValue: barData[idx],
            };
            linestr += `<div style="font-weight:bold; margin-bottom:6px;">${item.category}</div>`;
            linestr += `<div style="display:flex; justify-content:space-between;"><span style="color:${colors.textSecondary}">Actual:</span><span style="color:${colors.primary}; font-weight:bold">${item.actualValue}</span></div>`;
            // If curveMode aligns to actual or sorted-by-benford we have lineData aligned by index
            if (
              curveMode === "align_to_actual" ||
              curveMode === "sort_benford_desc"
            ) {
              const benval =
                lineData && lineData[idx] !== undefined ? lineData[idx] : 0;
              linestr += `<div style="display:flex; justify-content:space-between;"><span style="color:${
                colors.textSecondary
              }">Benford:</span><span style="color:${
                colors.secondary
              }; font-weight:bold">${Number(benval).toFixed(4)}</span></div>`;
            } else {
              // both mode: show the benford value at the hovered bar index if we can map it (we can't map directly), so show closest curve value
              if (lineDataNumeric.length > 0) {
                const benClosest = lineDataNumeric[idx]
                  ? lineDataNumeric[idx][1]
                  : lineDataNumeric[0][1];
                linestr += `<div style="display:flex; justify-content:space-between;"><span style="color:${
                  colors.textSecondary
                }">Benford (curve):</span><span style="color:${
                  colors.secondary
                }; font-weight:bold">${Number(benClosest).toFixed(
                  4
                )}</span></div>`;
              }
            }
            return linestr;
          } else {
            // fallback display
            const p = params[0];
            return `${p.seriesName}: ${p.value}`;
          }
        },
      },

      legend: {
        data: ["Actual", "Benford"],
        top: 8,
        textStyle: { color: colors.textPrimary },
      },

      xAxis: [
        // xAxis 0: categorical for bars
        {
          type: "category",
          data: barCategories,
          axisLabel: {
            rotate: 45,
            interval: 0,
            color: colors.textPrimary,
            fontSize: 11,
            fontWeight: "bold",
            formatter: (v) =>
              typeof v === "string" && v.length > 12
                ? v.substring(0, 12) + "..."
                : v,
          },
          axisLine: { lineStyle: { color: colors.gridLines } },
          axisTick: { alignWithLabel: true },
        },
        // xAxis 1: numeric spanning 0..n-1 for monotonic benford curve (hidden)
        {
          type: "value",
          min: 0,
          max: Math.max(n - 1, 0),
          show: false,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false },
        },
      ],

      yAxis: {
        type: "value",
        axisLabel: {
          color: colors.textPrimary,
          formatter: (v) => Number(v).toFixed(3),
        },
        axisLine: { lineStyle: { color: colors.gridLines } },
        splitLine: {
          lineStyle: { color: colors.gridLines, type: "dashed", opacity: 0.6 },
        },
        min: 0,
        max: Math.ceil(maxVal * 10) / 10 || undefined,
      },

      dataZoom: [
        {
          type: "slider",
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100,
          height: 20,
          bottom: 10,
        },
        { type: "inside", xAxisIndex: [0], start: 0, end: 100 },
      ],

      grid: {
        left: "4%",
        right: "4%",
        bottom: "20%",
        top: "15%",
        containLabel: true,
      },

      series: [
        {
          name: "Actual",
          type: "bar",
          data: barData,
          itemStyle: { color: colors.primary, borderRadius: [2, 2, 0, 0] },
          barWidth: "40%",
          label: {
            show: true,
            position: "top",
            formatter: "{c}",
            color: colors.textPrimary,
            fontSize: 9,
          },
          zlevel: 1,
          // uses xAxisIndex 0 by default
        },
        // Benford series: either aligned to category axis (lineData) or numeric x-axis (lineDataNumeric)
        curveMode === "both"
          ? {
              name: "Benford",
              type: "line",
              // use numeric xAxis (index 1)
              xAxisIndex: 1,
              yAxisIndex: 0,
              data: lineDataNumeric,
              smooth: true,
              sampling: "average",
              symbol: "none",
              lineStyle: {
                color: colors.secondary,
                width: 3,
                opacity: 0.95,
                shadowBlur: 4,
              },
              areaStyle: { opacity: 0.0 },
              zlevel: 3,
            }
          : {
              name: "Benford",
              type: "line",
              data: lineData,
              smooth: true,
              sampling: "average",
              symbol: "none",
              lineStyle: {
                color: colors.secondary,
                width: 3,
                opacity: 0.95,
                shadowBlur: 4,
              },
              areaStyle: { opacity: 0.0 },
              zlevel: 3,
            },
      ],
    };

    try {
      chartRef.current.setOption(option, true);
    } catch (e) {
      console.error("Error setting Benford chart option:", e);
    }
  }, [visibleData, currentRange, curveMode]);

  const totalItems = Array.isArray(data) ? data.length : 0;
  const maxSliderValue = Math.max(0, totalItems - 50);

  const handleSliderChange = (value) => {
    const start = Math.max(0, value);
    const end = Math.min(data.length, start + 50);
    setCurrentRange({ start, end });
  };
  const handlePrevious = () => {
    const newStart = Math.max(0, currentRange.start - 50);
    const newEnd = Math.min(data.length, newStart + 50);
    setCurrentRange({ start: newStart, end: newEnd });
  };
  const handleNext = () => {
    const newStart = Math.min(data.length - 50, currentRange.start + 50);
    const newEnd = Math.min(data.length, newStart + 50);
    setCurrentRange({ start: newStart, end: newEnd });
  };

  return (
    <div
      className="one-wrapper-BenFord"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={ref}
        style={{ width: "100%", height: "85%", borderRadius: "8px" }}
      />
      <div
        style={{
          height: "15%",
          padding: 10,
          background: "#f8f9fa",
          display: "flex",
          gap: 15,
          alignItems: "center",
        }}
      >
        <button
          onClick={handlePrevious}
          disabled={currentRange.start === 0}
          style={{
            padding: "8px 16px",
            background: currentRange.start === 0 ? "#bdc3c7" : "#3498db",
            color: "white",
            borderRadius: 4,
          }}
        >
          Previous
        </button>

        <div
          style={{ flex: 1, display: "flex", gap: 10, alignItems: "center" }}
        >
          <span style={{ minWidth: 60, color: colors.textPrimary }}>
            {currentRange.start + 1} - {Math.min(currentRange.end, totalItems)}{" "}
            of {totalItems}
          </span>
          <input
            type="range"
            min="0"
            max={maxSliderValue}
            value={currentRange.start}
            onChange={(e) => handleSliderChange(parseInt(e.target.value))}
            style={{ flex: 1, height: 6 }}
          />
        </div>

        <button
          onClick={handleNext}
          disabled={currentRange.end >= totalItems}
          style={{
            padding: "8px 16px",
            background: currentRange.end >= totalItems ? "#bdc3c7" : "#3498db",
            color: "white",
            borderRadius: 4,
          }}
        >
          Next
        </button>

        <div
          style={{
            fontSize: 11,
            color: colors.textSecondary,
            padding: "4px 8px",
            background: "white",
            borderRadius: 4,
            border: "1px solid #dee2e6",
          }}
        >
          Showing 50 items
        </div>
      </div>
    </div>
  );
};

export default BenfordChart;
